import {
  authenticatedFetch,
  resolvePeriverseApiBaseUrl,
} from '../shared';
import {
  CreateLocationDto,
  LocationResponseDto,
  LocationResponseDtoSchema,
  PaginatedLocationResponseDtoSchema,
  UpdateLocationDto,
} from '@peritasai/types';

type BaseUrlResolver = () => string;

export interface LocationsFilterParams {
  search?: string;
  page?: number;
  limit?: number;
  facilityId?: string;
}

export class LocationApiClient {
  constructor(
    private readonly resolveBaseUrl: BaseUrlResolver = resolvePeriverseApiBaseUrl
  ) {}

  async getLocations(
    params: LocationsFilterParams = {}
  ): Promise<{ data: LocationResponseDto[]; total: number; page: number; limit: number }> {
    const baseUrl = this.resolveBaseUrl();
    const query = new URLSearchParams();
    if (params.search) query.set('search', params.search);
    if (params.page != null) query.set('page', String(params.page));
    if (params.limit != null) query.set('limit', String(params.limit));
    if (params.facilityId) query.set('facilityId', params.facilityId);

    const url = `${baseUrl}/locations${query.toString() ? `?${query.toString()}` : ''}`;

    try {
      const response = await authenticatedFetch(url, {
        method: 'GET',
        headers: { accept: 'application/json' },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return PaginatedLocationResponseDtoSchema.parse(await response.json());
    } catch (error) {
      console.error('Error fetching locations:', error);
      throw error;
    }
  }

  async getLocationById(id: string): Promise<LocationResponseDto> {
    const baseUrl = this.resolveBaseUrl();
    const url = `${baseUrl}/locations/${id}`;

    try {
      const response = await authenticatedFetch(url, {
        method: 'GET',
        headers: { accept: 'application/json' },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return LocationResponseDtoSchema.parse(await response.json());
    } catch (error) {
      console.error(`Error fetching location ${id}:`, error);
      throw error;
    }
  }

  async createLocation(dto: CreateLocationDto): Promise<LocationResponseDto> {
    const baseUrl = this.resolveBaseUrl();
    const url = `${baseUrl}/locations`;

    try {
      const response = await authenticatedFetch(url, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dto),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error((body as { message?: string }).message ?? `HTTP error! status: ${response.status}`);
      }

      return LocationResponseDtoSchema.parse(await response.json());
    } catch (error) {
      console.error('Error creating location:', error);
      throw error;
    }
  }

  async updateLocation(id: string, dto: UpdateLocationDto): Promise<LocationResponseDto> {
    const baseUrl = this.resolveBaseUrl();
    const url = `${baseUrl}/locations/${id}`;

    try {
      const response = await authenticatedFetch(url, {
        method: 'PATCH',
        headers: {
          accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dto),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error((body as { message?: string }).message ?? `HTTP error! status: ${response.status}`);
      }

      return LocationResponseDtoSchema.parse(await response.json());
    } catch (error) {
      console.error(`Error updating location ${id}:`, error);
      throw error;
    }
  }
}

export const createLocationApiClient = (
  resolveBaseUrl: BaseUrlResolver = resolvePeriverseApiBaseUrl
): LocationApiClient => new LocationApiClient(resolveBaseUrl);

export const locationApiClient = new LocationApiClient();

export type { BaseUrlResolver };
